import React, { useEffect, useRef } from 'react';
import { allowedCharLettersForProduct, normalizePersonalizationLetter } from '../productPersonalization';
import './char-picker.css';

function LetterChip({ letter, active, disabled, id, compact, onSelect }) {
  const cls = compact
    ? `char-picker__btn char-picker__btn--compact${active ? ' char-picker__btn--active' : ''}`
    : `char-picker__btn${active ? ' char-picker__btn--active' : ''}`;

  return (
    <button
      type="button"
      id={id}
      className={cls}
      aria-pressed={active}
      aria-label={`Letter ${letter}`}
      disabled={disabled}
      onClick={() => onSelect(letter)}
    >
      {letter}
    </button>
  );
}

export default function CharacterPicker({
  product,
  value,
  onChange,
  disabled,
  idPrefix = 'char',
  compact = false,
  /** Single-line letter strip on product cards (featured, grids) — keeps card height even. */
  gridMinimal = false,
}) {
  const letters = allowedCharLettersForProduct(product);
  const selected = normalizePersonalizationLetter(value);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!compact || !selected || !scrollRef.current) return;
    const btn = scrollRef.current.querySelector(`#${CSS.escape(`${idPrefix}-${selected}`)}`);
    btn?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [compact, selected, idPrefix]);

  if (compact && gridMinimal) {
    return (
      <div
        className={`char-picker char-picker--compact char-picker--grid-minimal${
          selected ? ' char-picker--has-selection' : ''
        }`}
        role="group"
        aria-labelledby={`${idPrefix}-label`}
      >
        <span className="char-picker__grid-label" id={`${idPrefix}-label`}>
          Letter
        </span>
        <div className="char-picker__compact-scroll-wrap">
          <div className="char-picker__compact-scroll" ref={scrollRef}>
            <div className="char-picker__grid char-picker__grid--compact">
              {letters.map((letter) => (
                <LetterChip
                  key={letter}
                  letter={letter}
                  active={selected === letter}
                  disabled={disabled}
                  id={`${idPrefix}-${letter}`}
                  compact
                  onSelect={onChange}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={`char-picker char-picker--compact${selected ? ' char-picker--has-selection' : ''}`}
        role="group"
        aria-label="Choose your monogram letter"
      >
        <div className="char-picker__compact-head">
          <span className="char-picker__compact-mark" aria-hidden>
            ✦
          </span>
          <div className="char-picker__compact-copy">
            <span className="char-picker__compact-title">Your monogram</span>
            <span className="char-picker__compact-hint">
              {selected ? `Letter ${selected} selected` : 'Tap one letter'}
            </span>
          </div>
          <span
            className={`char-picker__preview${selected ? ' char-picker__preview--chosen' : ' char-picker__preview--empty'}`}
            aria-live="polite"
            title={selected ? `Selected: ${selected}` : 'No letter chosen yet'}
          >
            {selected || '?'}
          </span>
        </div>
        <div className="char-picker__compact-scroll-wrap">
          <div className="char-picker__compact-scroll" ref={scrollRef}>
            <div className="char-picker__grid char-picker__grid--compact">
              {letters.map((letter) => (
                <LetterChip
                  key={letter}
                  letter={letter}
                  active={selected === letter}
                  disabled={disabled}
                  id={`${idPrefix}-${letter}`}
                  compact
                  onSelect={onChange}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`char-picker char-picker--detail${selected ? ' char-picker--has-selection' : ''}`}
      role="group"
      aria-label="Choose your monogram letter"
    >
      <div className="char-picker__detail-head">
        <span className="char-picker__detail-icon" aria-hidden>
          ✦
        </span>
        <div className="char-picker__detail-text">
          <p className="char-picker__label">Your monogram letter</p>
          <p className="char-picker__lead">
            Select the initial we&apos;ll weave into your piece — made to feel personal, like a gift chosen just for
            them.
          </p>
        </div>
        <span
          className={`char-picker__preview char-picker__preview--detail${
            selected ? ' char-picker__preview--chosen' : ' char-picker__preview--empty'
          }`}
          aria-live="polite"
        >
          {selected || '?'}
        </span>
      </div>
      <div className="char-picker__detail-divider" aria-hidden />
      <div className="char-picker__grid char-picker__grid--detail">
        {letters.map((letter) => (
          <LetterChip
            key={letter}
            letter={letter}
            active={selected === letter}
            disabled={disabled}
            id={`${idPrefix}-${letter}`}
            compact={false}
            onSelect={onChange}
          />
        ))}
      </div>
    </div>
  );
}
